import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Save, GripVertical, Plus, Trash2 } from "lucide-react";
import type { Json } from "@/integrations/supabase/types";

interface Question {
  id: string;
  label: string;
  helper_text: string | null;
  input_type: string;
  sort_order: number;
  prompt_variable: string | null;
  options: Json;
  is_required: boolean;
  slider_min: number | null;
  slider_max: number | null;
  slider_labels: Json;
  questionnaire_id: string;
}

interface Questionnaire {
  id: string;
  title: string;
  visualization_type: string;
  is_active: boolean;
}

const INPUT_TYPES = ["text", "textarea", "select", "multi_select", "slider", "radio"];

const AdminQuestionnaires = () => {
  const [questionnaires, setQuestionnaires] = useState<Questionnaire[]>([]);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedQ, setSelectedQ] = useState<Questionnaire | null>(null);
  const [saving, setSaving] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const { data: qs } = await supabase
      .from("questionnaires")
      .select("*")
      .order("visualization_type");
    if (qs) {
      setQuestionnaires(qs as Questionnaire[]);
      if (qs.length > 0 && !selectedQ) {
        selectQuestionnaire(qs[0] as Questionnaire);
      }
    }
    setLoading(false);
  };

  const selectQuestionnaire = async (q: Questionnaire) => {
    setSelectedQ(q);
    setEditingQuestion(null);
    const { data } = await supabase
      .from("questions")
      .select("*")
      .eq("questionnaire_id", q.id)
      .order("sort_order");
    if (data) setQuestions(data as Question[]);
  };

  const handleSaveQuestion = async (question: Question) => {
    setSaving(true);
    const { error } = await supabase
      .from("questions")
      .update({
        label: question.label,
        helper_text: question.helper_text,
        input_type: question.input_type as any,
        prompt_variable: question.prompt_variable,
        options: question.options,
        is_required: question.is_required,
        slider_min: question.slider_min,
        slider_max: question.slider_max,
        slider_labels: question.slider_labels,
        sort_order: question.sort_order,
      })
      .eq("id", question.id);
    if (error) toast.error("Failed to save question");
    else {
      toast.success("Question saved");
      setQuestions((prev) => prev.map((q) => (q.id === question.id ? question : q)));
    }
    setSaving(false);
    setEditingQuestion(null);
  };

  const handleDeleteQuestion = async (id: string) => {
    if (!confirm("Delete this question?")) return;
    await supabase.from("questions").delete().eq("id", id);
    setQuestions((prev) => prev.filter((q) => q.id !== id));
    toast.success("Question deleted");
  };

  const handleAddQuestion = async () => {
    if (!selectedQ) return;
    const newOrder = questions.length > 0 ? Math.max(...questions.map((q) => q.sort_order)) + 1 : 1;
    const { data, error } = await supabase
      .from("questions")
      .insert({
        questionnaire_id: selectedQ.id,
        label: "New question",
        input_type: "text" as any,
        sort_order: newOrder,
        is_required: true,
        prompt_variable: "new_variable",
      })
      .select()
      .single();
    if (error) toast.error("Failed to add question");
    else if (data) {
      setQuestions((prev) => [...prev, data as Question]);
      setEditingQuestion(data as Question);
      toast.success("Question added");
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="font-display text-3xl font-bold">Questionnaires</h1>
          <p className="text-muted-foreground mt-1">
            Manage the questions users answer before generating a visualization.
          </p>
        </div>

        {/* Questionnaire tabs */}
        <div className="flex gap-2">
          {questionnaires.map((q) => (
            <button
              key={q.id}
              onClick={() => selectQuestionnaire(q)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors capitalize ${
                selectedQ?.id === q.id
                  ? "bg-primary text-primary-foreground"
                  : "bg-secondary/50 text-muted-foreground hover:bg-secondary"
              }`}
            >
              {q.visualization_type}
            </button>
          ))}
        </div>

        {selectedQ && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-semibold">{selectedQ.title}</h2>
              <Button onClick={handleAddQuestion} variant="outline" size="sm">
                <Plus className="w-4 h-4 mr-1" /> Add Question
              </Button>
            </div>

            <div className="space-y-3">
              {questions.map((q, idx) => (
                <div
                  key={q.id}
                  className={`rounded-xl border p-4 transition-colors ${
                    editingQuestion?.id === q.id
                      ? "border-primary bg-primary/5"
                      : "border-border/50 bg-secondary/30"
                  }`}
                >
                  {editingQuestion?.id === q.id ? (
                    <QuestionEditor
                      question={editingQuestion}
                      onChange={setEditingQuestion}
                      onSave={() => handleSaveQuestion(editingQuestion)}
                      onCancel={() => setEditingQuestion(null)}
                      saving={saving}
                    />
                  ) : (
                    <div
                      className="flex items-start justify-between cursor-pointer"
                      onClick={() => setEditingQuestion({ ...q })}
                    >
                      <div className="flex items-start gap-3">
                        <GripVertical className="w-4 h-4 text-muted-foreground mt-1 flex-shrink-0" />
                        <div>
                          <p className="font-medium text-sm">{q.label}</p>
                          <div className="flex gap-2 mt-1">
                            <span className="text-xs px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">
                              {q.input_type}
                            </span>
                            <span className="text-xs font-mono text-muted-foreground">
                              {`{{${q.prompt_variable}}}`}
                            </span>
                            {q.is_required && (
                              <span className="text-xs text-primary">Required</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteQuestion(q.id);
                        }}
                        className="text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

const QuestionEditor = ({
  question,
  onChange,
  onSave,
  onCancel,
  saving,
}: {
  question: Question;
  onChange: (q: Question) => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
}) => {
  const optionsStr = Array.isArray(question.options)
    ? (question.options as string[]).join("\n")
    : "";
  const sliderLabelsStr = question.slider_labels
    ? JSON.stringify(question.slider_labels, null, 2)
    : "";

  return (
    <div className="space-y-3">
      <input
        value={question.label}
        onChange={(e) => onChange({ ...question, label: e.target.value })}
        placeholder="Question label"
        className="w-full h-10 rounded-lg border border-border/50 bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
      />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <label className="text-xs text-muted-foreground">Input Type</label>
          <select
            value={question.input_type}
            onChange={(e) => onChange({ ...question, input_type: e.target.value })}
            className="w-full rounded-lg border border-border/50 bg-background px-3 py-2 text-sm"
          >
            {INPUT_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Prompt Variable</label>
          <input
            value={question.prompt_variable || ""}
            onChange={(e) => onChange({ ...question, prompt_variable: e.target.value })}
            className="w-full h-9 rounded-lg border border-border/50 bg-background px-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Sort Order</label>
          <input
            type="number"
            value={question.sort_order}
            onChange={(e) => onChange({ ...question, sort_order: parseInt(e.target.value) || 0 })}
            className="w-full h-9 rounded-lg border border-border/50 bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={question.is_required}
              onChange={(e) => onChange({ ...question, is_required: e.target.checked })}
            />
            Required
          </label>
        </div>
      </div>

      <input
        value={question.helper_text || ""}
        onChange={(e) => onChange({ ...question, helper_text: e.target.value || null })}
        placeholder="Helper text (optional)"
        className="w-full h-9 rounded-lg border border-border/50 bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
      />

      {(question.input_type === "select" || question.input_type === "multi_select" || question.input_type === "radio") && (
        <div>
          <label className="text-xs text-muted-foreground">Options (one per line)</label>
          <textarea
            value={optionsStr}
            onChange={(e) =>
              onChange({
                ...question,
                options: e.target.value.split("\n").filter((l) => l.trim()) as any,
              })
            }
            className="w-full h-24 rounded-lg border border-border/50 bg-background p-3 text-sm font-mono resize-none focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
      )}

      {question.input_type === "slider" && (
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-muted-foreground">Min</label>
            <input
              type="number"
              value={question.slider_min ?? ""}
              onChange={(e) => onChange({ ...question, slider_min: parseInt(e.target.value) || null })}
              className="w-full h-9 rounded-lg border border-border/50 bg-background px-3 text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Max</label>
            <input
              type="number"
              value={question.slider_max ?? ""}
              onChange={(e) => onChange({ ...question, slider_max: parseInt(e.target.value) || null })}
              className="w-full h-9 rounded-lg border border-border/50 bg-background px-3 text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Labels (JSON)</label>
            <textarea
              value={sliderLabelsStr}
              onChange={(e) => {
                try {
                  onChange({ ...question, slider_labels: JSON.parse(e.target.value) });
                } catch {}
              }}
              className="w-full h-20 rounded-lg border border-border/50 bg-background p-2 text-xs font-mono resize-none"
            />
          </div>
        </div>
      )}

      <div className="flex gap-2">
        <Button onClick={onSave} disabled={saving} size="sm">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
          Save
        </Button>
        <Button onClick={onCancel} variant="ghost" size="sm">
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default AdminQuestionnaires;
